<template>
	<div class="doctors-section">
		<div class="main">
			<div class="header">
				<div class="header-left">
					<b>Participants</b>
					<h1>参与医生</h1>
				</div>
				<div class="stats-row" v-if="doctors.length > 0">
					<div class="stat-item">
						<b>{{ doctors.length }}</b>
						<i>参与医生</i>
					</div>
					<div class="stat-item">
						<b>{{ hospitalsCount }}</b>
						<i>覆盖医院</i>
					</div>
					<div class="stat-item">
						<b>{{ departments.length }}</b>
						<i>专业科室</i>
					</div>
				</div>
			</div>

			<div class="toolbar" v-if="doctors.length > 0">
				<div class="search-box">
					<i class="el-icon-search"></i>
					<input
						type="text"
						v-model="searchText"
						placeholder="搜索医生姓名或医院"
					/>
				</div>
				<div class="dept-filter">
					<span
						class="filter-tag"
						:class="{ active: activeDept === '' }"
						@click="activeDept = ''; currentPage = 1"
					>全部</span>
					<span
						v-for="dept in departments"
						:key="dept"
						class="filter-tag"
						:class="{ active: activeDept === dept }"
						@click="activeDept = dept; currentPage = 1"
					>{{ dept }}</span>
				</div>
			</div>

			<div class="doctor-grid">
				<div
					class="doctor-card"
					v-for="(doctor, index) in pagedDoctors"
					:key="index"
				>
					<div class="avatar" :style="{ background: getAvatarColor(doctor.name) }">
						{{ doctor.name.charAt(0) }}
					</div>
					<div class="info">
						<h3>{{ doctor.name }}</h3>
						<p class="hospital">
							<i class="el-icon-office-building"></i>
							{{ doctor.hospital }}
						</p>
						<p class="department">
							<i class="el-icon-first-aid-kit"></i>
							{{ doctor.department }}
						</p>
					</div>
				</div>
			</div>

			<div v-if="filteredDoctors.length === 0" class="empty-state">
				<i class="el-icon-user"></i>
				<p>暂无匹配的医生信息</p>
			</div>

			<div class="pagination-wrap" v-if="totalPages > 1">
				<span
					class="page-btn prev"
					:class="{ disabled: currentPage <= 1 }"
					@click="currentPage > 1 && currentPage--"
				>
					<i class="el-icon-arrow-left"></i>
				</span>

				<template v-for="p in visiblePages">
					<span
						v-if="p !== '...'"
						:key="p"
						class="page-btn"
						:class="{ active: currentPage === p }"
						@click="currentPage = p"
					>{{ p }}</span>
					<span v-else :key="'e' + p" class="page-ellipsis">...</span>
				</template>

				<span
					class="page-btn next"
					:class="{ disabled: currentPage >= totalPages }"
					@click="currentPage < totalPages && currentPage++"
				>
					<i class="el-icon-arrow-right"></i>
				</span>

				<span class="page-info">共 {{ filteredDoctors.length }} 位医生</span>
			</div>
		</div>
	</div>
</template>

<script>
	import { getDoctorList } from '@/request/api.js'
	export default {
		name: 'homePartDoctors',
		data() {
			return {
				mainColor: '',
				activeDept: '',
				searchText: '',
				currentPage: 1,
				pageSize: 12,
				doctors: []
			}
		},
		computed: {
			departments() {
				const depts = [...new Set(this.doctors.map(d => d.department).filter(Boolean))]
				return depts.sort()
			},
			hospitalsCount() {
				return [...new Set(this.doctors.map(d => d.hospital).filter(Boolean))].length
			},
			filteredDoctors() {
				let list = this.doctors
				if (this.activeDept) {
					list = list.filter(d => d.department === this.activeDept)
				}
				if (this.searchText.trim()) {
					const kw = this.searchText.trim().toLowerCase()
					list = list.filter(d =>
						(d.name && d.name.toLowerCase().includes(kw)) ||
						(d.hospital && d.hospital.toLowerCase().includes(kw))
					)
				}
				return list
			},
			totalPages() {
				return Math.ceil(this.filteredDoctors.length / this.pageSize)
			},
			pagedDoctors() {
				const start = (this.currentPage - 1) * this.pageSize
				return this.filteredDoctors.slice(start, start + this.pageSize)
			},
			visiblePages() {
				const total = this.totalPages
				const cur = this.currentPage
				if (total <= 7) {
					return Array.from({ length: total }, (_, i) => i + 1)
				}
				const pages = []
				pages.push(1)
				if (cur > 3) pages.push('...')
				const start = Math.max(2, cur - 1)
				const end = Math.min(total - 1, cur + 1)
				for (let i = start; i <= end; i++) pages.push(i)
				if (cur < total - 2) pages.push('...')
				pages.push(total)
				return pages
			}
		},
		watch: {
			searchText() {
				this.currentPage = 1
			}
		},
		created() {
			this.mainColor = localStorage.getItem('mainColor')
			this.fetchDoctors()
		},
		updated() {
			this.mainColor = localStorage.getItem('mainColor')
		},
		methods: {
			fetchDoctors() {
				getDoctorList().then(res => {
					if (res.code == 200) {
						this.doctors = res.data || []
					}
				}).catch(() => {})
			},
			getAvatarColor(name) {
				if (!name) return '#999'
				const colors = [
					'#4A90D9', '#67B8A7', '#8B7EC8', '#D4886B',
					'#5BA08D', '#7A9BD5', '#C07BAA', '#6AAF6A',
					'#D4955A', '#8AAED0', '#A0886E', '#6BB5B5'
				]
				let hash = 0
				for (let i = 0; i < name.length; i++) {
					hash = name.charCodeAt(i) + ((hash << 5) - hash)
				}
				return colors[Math.abs(hash) % colors.length]
			}
		}
	}
</script>

<style lang="scss" scoped>
	$main-color: v-bind(mainColor);

	.doctors-section {
		padding: 150px 0;
		background: #fff;

		.main {
			width: 1200px;
			margin: auto;
		}

		// === Header ===
		.header {
			display: flex;
			justify-content: space-between;
			align-items: flex-end;
			margin-bottom: 60px;

			.header-left {
				b {
					color: $main-color;
					font-size: 16px;
				}

				h1 {
					font-size: 70px;
					font-weight: 600;
					line-height: 1.5;
				}
			}

			.stats-row {
				display: flex;
				gap: 50px;

				.stat-item {
					display: flex;
					flex-direction: column;
					align-items: center;

					b {
						font-size: 40px;
						color: $main-color;
						font-weight: 600;
					}

					i {
						font-style: normal;
						color: #999;
						font-size: 14px;
						margin-top: 4px;
					}
				}
			}
		}

		// === Toolbar ===
		.toolbar {
			margin-bottom: 40px;

			.search-box {
				display: flex;
				align-items: center;
				gap: 10px;
				background: #f5f6f8;
				border-radius: 12px;
				padding: 0 20px;
				height: 50px;
				margin-bottom: 20px;

				i {
					font-size: 18px;
					color: #bbb;
				}

				input {
					flex: 1;
					border: none;
					background: transparent;
					font-size: 15px;
					color: #333;
					outline: none;

					&::placeholder {
						color: #ccc;
					}
				}
			}

			.dept-filter {
				display: flex;
				flex-wrap: wrap;
				gap: 10px;

				.filter-tag {
					padding: 8px 22px;
					border-radius: 20px;
					font-size: 14px;
					color: #666;
					background: #f5f6f8;
					cursor: pointer;
					transition: all 0.3s;

					&:hover {
						color: $main-color;
					}

					&.active {
						background: $main-color;
						color: #fff;
					}
				}
			}
		}

		// === Card Grid ===
		.doctor-grid {
			display: grid;
			grid-template-columns: repeat(4, 1fr);
			gap: 20px;
		}

		.doctor-card {
			background: #fff;
			border-radius: 16px;
			padding: 24px 20px;
			display: flex;
			align-items: flex-start;
			gap: 16px;
			transition: all 0.3s;
			box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

			&:hover {
				box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
				transform: translateY(-2px);
			}

			.avatar {
				width: 48px;
				height: 48px;
				border-radius: 50%;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 18px;
				font-weight: 600;
				color: #fff;
				flex-shrink: 0;
			}

			.info {
				flex: 1;
				min-width: 0;

				h3 {
					font-size: 16px;
					font-weight: 600;
					color: #333;
					margin-bottom: 6px;
					line-height: 1.4;
				}

				p {
					font-size: 13px;
					color: #999;
					line-height: 1.6;
					display: flex;
					align-items: center;
					gap: 4px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;

					i {
						font-size: 12px;
						flex-shrink: 0;
					}
				}

				.hospital {
					color: #666;
				}
			}
		}

		// === Empty ===
		.empty-state {
			text-align: center;
			padding: 100px 0;
			color: #ccc;

			i {
				font-size: 48px;
				display: block;
				margin-bottom: 16px;
			}

			p {
				font-size: 15px;
			}
		}

		// === Pagination ===
		.pagination-wrap {
			margin-top: 50px;
			display: flex;
			justify-content: center;
			align-items: center;
			gap: 8px;

			.page-btn {
				width: 40px;
				height: 40px;
				display: flex;
				align-items: center;
				justify-content: center;
				border-radius: 10px;
				font-size: 15px;
				color: #666;
				cursor: pointer;
				transition: all 0.25s;
				background: #f5f6f8;

				&:hover:not(.disabled):not(.active) {
					color: $main-color;
				}

				&.active {
					background: $main-color;
					color: #fff;
					font-weight: 600;
				}

				&.disabled {
					color: #ccc;
					cursor: not-allowed;
				}

				i {
					font-size: 14px;
				}
			}

			.page-ellipsis {
				width: 40px;
				height: 40px;
				display: flex;
				align-items: center;
				justify-content: center;
				color: #ccc;
				font-size: 14px;
			}

			.page-info {
				margin-left: 16px;
				font-size: 14px;
				color: #999;
			}
		}
	}
</style>
